import { MovementType, adjustStockSchema, type AdjustStockInput } from '@lombok/shared';

/**
 * What the user picks in the stock form. The quantity is always typed as a positive number;
 * the choice decides whether it adds or subtracts, so nobody has to type a minus sign.
 */
export type MovementChoice = 'receive' | 'add' | 'remove';

export const MOVEMENTS: Record<
  MovementChoice,
  { label: string; type: AdjustStockInput['type']; sign: 1 | -1; hint: string }
> = {
  receive: {
    label: 'Entrada de mercancía (suma)',
    type: MovementType.RECEIVE,
    sign: 1,
    hint: 'Unidades que ingresan al inventario. Esta cantidad se sumará.',
  },
  add: {
    label: 'Ajuste de conteo: sumar',
    type: MovementType.ADJUSTMENT,
    sign: 1,
    hint: 'Esta cantidad se sumará al inventario.',
  },
  remove: {
    label: 'Ajuste de conteo: descontar',
    type: MovementType.ADJUSTMENT,
    sign: -1,
    hint: 'Esta cantidad se descontará (restará) del inventario disponible.',
  },
};

export type AdjustFieldErrors = Partial<
  Record<'sku' | 'locationId' | 'quantity' | 'reason' | 'reorderPoint', string>
>;

export interface AdjustFormValues {
  choice: MovementChoice;
  skuId: string;
  locationId: string;
  quantityText: string;
  reason: string;
  reorderText: string;
}

export type AdjustFormResult =
  | { ok: true; input: AdjustStockInput }
  | { ok: false; errors: AdjustFieldErrors };

/**
 * Turn the form's raw text into the signed `AdjustStockInput` the API takes, or per-field
 * Spanish errors. Validation is the shared `adjustStockSchema` (the contract the API enforces);
 * this only adds the form's own rule — the typed amount is positive and the movement type
 * supplies the sign.
 */
export function toAdjustStockInput(values: AdjustFormValues): AdjustFormResult {
  const { type, sign } = MOVEMENTS[values.choice];
  const quantity = values.quantityText.trim() === '' ? Number.NaN : Number(values.quantityText);
  const reorder = values.reorderText.trim();

  const errors: AdjustFieldErrors = {};
  if (Number.isNaN(quantity)) {
    errors.quantity = 'Indica una cantidad entera.';
  } else if (quantity < 0) {
    errors.quantity = 'Escribe la cantidad sin signo negativo y elige "descontar" en el tipo de movimiento.';
  }

  const parsed = adjustStockSchema.safeParse({
    skuId: values.skuId,
    locationId: values.locationId.trim(),
    type,
    quantity: quantity * sign,
    reason: values.reason,
    reorderPoint: reorder === '' ? undefined : Number(reorder),
  });
  if (parsed.success && !errors.quantity) return { ok: true, input: parsed.data };

  for (const issue of parsed.success ? [] : parsed.error.issues) {
    const key = issue.path[0];
    if (key === 'skuId') errors.sku ??= 'Selecciona la presentación (SKU).';
    else if (key === 'locationId') errors.locationId ??= 'Indica la ubicación.';
    else if (key === 'quantity') {
      // The schema works on signed values, so its "0" message is reworded for the positive field.
      errors.quantity ??=
        issue.message === 'La cantidad no puede ser 0.' ? 'La cantidad debe ser mayor que 0.' : issue.message;
    } else if (key === 'reason') errors.reason ??= issue.message;
    else if (key === 'reorderPoint') errors.reorderPoint ??= 'El punto de reorden debe ser un entero de 0 o más.';
  }
  return { ok: false, errors };
}
